/**
 * Carbon Form Wrapper Component
 * Provides standardized form layout and behavior with Carbon Design System
 * Implements accessibility features and consistent error handling
 */

import React, { FormEvent, ReactNode } from 'react';
import {
  Form,
  Stack,
  InlineNotification,
  ProgressIndicator,
  ProgressStep,
  Loading,
  Button,
  Grid,
  Column,
} from '@carbon/react';
// Removed framer-motion dependency - using CSS animations instead
import './CarbonFormWrapper.module.scss';

export interface FormStep {
  id: string;
  label: string;
  description?: string;
  optional?: boolean;
  invalid?: boolean;
  disabled?: boolean;
  complete?: boolean;
}

export interface CarbonFormWrapperProps {
  children: ReactNode;
  onSubmit: (e: FormEvent) => void | Promise<void>;
  loading?: boolean;
  error?: string | null;
  success?: string | null;
  warning?: string | null;
  info?: string | null;

  // Multi-step form props
  steps?: FormStep[];
  currentStep?: number;
  onStepChange?: (step: number) => void;

  // Form layout props
  maxWidth?: number | string;
  spacing?: 'condensed' | 'normal' | 'wide';

  // Accessibility props
  formLabel?: string;
  formDescription?: string;

  // Submission props
  submitLabel?: string;
  cancelLabel?: string;
  onCancel?: () => void;
  showButtons?: boolean;
  buttonsAlignment?: 'left' | 'center' | 'right' | 'space-between';

  // Additional props
  className?: string;
  id?: string;
  noValidate?: boolean;
}

const CarbonFormWrapper: React.FC<CarbonFormWrapperProps> = ({
  children,
  onSubmit,
  loading = false,
  error,
  success,
  warning,
  info,
  steps,
  currentStep = 0,
  onStepChange,
  maxWidth = 600,
  spacing = 'normal',
  formLabel,
  formDescription,
  submitLabel = 'Submit',
  cancelLabel = 'Cancel',
  onCancel,
  showButtons = true,
  buttonsAlignment = 'right',
  className = '',
  id,
  noValidate = true,
}) => {
  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    e.stopPropagation();
    await onSubmit(e);
  };

  const getSpacingClass = () => {
    switch (spacing) {
      case 'condensed':
        return 'carbon-form--condensed';
      case 'wide':
        return 'carbon-form--wide';
      default:
        return 'carbon-form--normal';
    }
  };

  const getButtonAlignment = () => {
    switch (buttonsAlignment) {
      case 'left':
        return 'flex-start';
      case 'center':
        return 'center';
      case 'space-between':
        return 'space-between';
      default:
        return 'flex-end';
    }
  };

  return (
    <Grid className={`carbon-form-wrapper ${className} ${getSpacingClass()}`} narrow>
      <Column sm={4} md={8} lg={12} xlg={12} max={16}>
        {/* Progress Indicator for multi-step forms */}
        {steps && steps.length > 0 && (
          <div className="carbon-form-wrapper__progress" role="navigation" aria-label="Form progress">
            <ProgressIndicator currentIndex={currentStep} onChange={onStepChange} spaceEqually>
              {steps.map((step, index) => (
                <ProgressStep
                  key={step.id}
                  label={step.label}
                  description={step.description}
                  invalid={step.invalid}
                  disabled={step.disabled}
                  complete={step.complete || index < currentStep}
                  current={index === currentStep}
                />
              ))}
            </ProgressIndicator>
          </div>
        )}

        {/* Form Header */}
        {(formLabel || formDescription) && (
          <div className="carbon-form-wrapper__header">
            {formLabel && (
              <h2 className="carbon-form-wrapper__title" id={`${id}-title`}>
                {formLabel}
              </h2>
            )}
            {formDescription && (
              <p className="carbon-form-wrapper__description" id={`${id}-description`}>
                {formDescription}
              </p>
            )}
          </div>
        )}

        {/* Notifications */}
        {(error || success || warning || info) && (
          <div className="carbon-form-wrapper__notifications carbon-form-wrapper__notifications--fade-in">
            <Stack gap={4}>
              {error && (
                <InlineNotification
                  kind="error"
                  title="Error"
                  subtitle={error}
                  hideCloseButton={false}
                  role="alert"
                  aria-live="assertive"
                />
              )}
              {success && (
                <InlineNotification
                  kind="success"
                  title="Success"
                  subtitle={success}
                  hideCloseButton={false}
                  role="status"
                  aria-live="polite"
                />
              )}
              {warning && (
                <InlineNotification
                  kind="warning"
                  title="Warning"
                  subtitle={warning}
                  hideCloseButton={false}
                  role="status"
                  aria-live="polite"
                />
              )}
              {info && (
                <InlineNotification
                  kind="info"
                  title="Information"
                  subtitle={info}
                  hideCloseButton={false}
                  role="status"
                  aria-live="polite"
                />
              )}
            </Stack>
          </div>
        )}

        {/* Form Content */}
        <Form
          id={id}
          onSubmit={handleSubmit}
          noValidate={noValidate}
          aria-labelledby={formLabel ? `${id}-title` : undefined}
          aria-describedby={formDescription ? `${id}-description` : undefined}
        >
          <Grid narrow>
            <Column sm={4} md={8} lg={12}>
              <div className="carbon-form-wrapper__content">
                {loading ? (
                  <div className="carbon-form-wrapper__loading">
                    <Loading description="Processing..." withOverlay={false} />
                  </div>
                ) : (
                  children
                )}
              </div>
            </Column>
          </Grid>

          {/* Form Buttons */}
          {showButtons && !loading && (
            <Grid narrow>
              <Column sm={4} md={8} lg={12}>
                <div className="carbon-form-wrapper__buttons" style={{ justifyContent: getButtonAlignment() }}>
                  {onCancel && (
                    <Button kind="secondary" onClick={onCancel} disabled={loading}>
                      {cancelLabel}
                    </Button>
                  )}
                  <Button kind="primary" type="submit" disabled={loading}>
                    {submitLabel}
                  </Button>
                </div>
              </Column>
            </Grid>
          )}
        </Form>
      </Column>
    </Grid>
  );
};

/**
 * Multi-step form container component
 */
export const CarbonMultiStepForm: React.FC<{
  children: ReactNode[];
  steps: FormStep[];
  onComplete: (data: any) => void | Promise<void>;
  onStepValidate?: (step: number, data: any) => Promise<boolean>;
  className?: string;
}> = ({ children, steps, onComplete, onStepValidate, className = '' }) => {
  const [currentStep, setCurrentStep] = React.useState(0);
  const [stepData, setStepData] = React.useState<Record<number, any>>({});
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);

  const handleNext = async () => {
    if (onStepValidate) {
      setLoading(true);
      try {
        const isValid = await onStepValidate(currentStep, stepData[currentStep]);
        if (!isValid) {
          setError('Please complete all required fields');
          return;
        }
      } catch (err: any) {
        setError(err.message || 'Validation failed');
        return;
      } finally {
        setLoading(false);
      }
    }

    if (currentStep < steps.length - 1) {
      setCurrentStep(currentStep + 1);
      setError(null);
    } else {
      // Final step - submit all data
      const allData = Object.values(stepData).reduce((acc, data) => ({ ...acc, ...data }), {});
      await onComplete(allData);
    }
  };

  const handleBack = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
      setError(null);
    }
  };

  const handleStepChange = (step: number) => {
    if (step < currentStep || step === 0) {
      setCurrentStep(step);
      setError(null);
    }
  };

  const updateStepData = (data: any) => {
    setStepData(prev => ({
      ...prev,
      [currentStep]: data,
    }));
  };

  return (
    <CarbonFormWrapper
      className={`carbon-multi-step-form ${className}`}
      steps={steps}
      currentStep={currentStep}
      onStepChange={handleStepChange}
      onSubmit={handleNext}
      error={error}
      loading={loading}
      submitLabel={currentStep === steps.length - 1 ? 'Complete' : 'Next'}
      cancelLabel="Back"
      onCancel={currentStep > 0 ? handleBack : undefined}
      showButtons={true}
    >
      <div key={currentStep} className="carbon-form-step">
        {React.cloneElement(children[currentStep] as React.ReactElement, {
          onChange: updateStepData,
          data: stepData[currentStep] || {},
        })}
      </div>
    </CarbonFormWrapper>
  );
};

/**
 * Form section component for logical grouping
 */
export const CarbonFormSection: React.FC<{
  title?: string;
  description?: string;
  children: ReactNode;
  optional?: boolean;
  className?: string;
}> = ({ title, description, children, optional = false, className = '' }) => {
  return (
    <div className={`carbon-form-section ${className}`}>
      {title && (
        <div className="carbon-form-section__header">
          <h3 className="carbon-form-section__title">
            {title}
            {optional && <span className="carbon-form-section__optional"> (Optional)</span>}
          </h3>
          {description && <p className="carbon-form-section__description">{description}</p>}
        </div>
      )}
      <div className="carbon-form-section__content">{children}</div>
    </div>
  );
};

export default CarbonFormWrapper;
